import { supabase } from "./supabase";
import { enfileirar, enfileirarVarios, enviarPendentes } from "./filaGravacao";
import { semColunasGeradas } from "./colunasGeradas";

// ── Conversão de chaves: snake_case camelCase ──
const toSnake = (s) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

// ── As colunas que a conversão automática NÃO acerta ──
//
// `toSnake` quebra antes de LETRA maiúscula e `toCamel` desfaz. Nenhum dos dois
// enxerga DÍGITO: `atende24h` ia para o banco como `atende24h` (a coluna é
// `atende_24h`), e na volta `atende_24h` ficava intacto (o app procura
// `atende24h`).
//
// O estrago era grande e silencioso: quando uma coluna não existe, o Postgres
// recusa a LINHA INTEIRA (PGRST204) — então toda gravação vinda do formulário
// do CRM falhava, e a ficha não salvava. O link público escapava porque lá os
// campos são escritos à mão, já em snake_case. Sintoma na tela: "Não foi
// possível salvar agora (conexão ou servidor instável)", que culpava a rede por
// um erro de nome de coluna.
//
// Mapa explícito, e não uma regra nova no conversor: ensiná-lo a quebrar em
// dígitos mudaria o nome de qualquer chave com número, em todas as tabelas,
// para resolver um caso conhecido.
const ALIAS_COLUNA = { atende24h: "atende_24h" };
const ALIAS_CAMPO = Object.fromEntries(Object.entries(ALIAS_COLUNA).map(([campo, coluna]) => [coluna, campo]));

// Converte apenas chaves de 1º nível (JSONB permanece intacto)
const rowFromDb = (row) => {
  if (!row) return null;
  const out = {};
  for (const [k, v] of Object.entries(row)) out[ALIAS_CAMPO[k] || toCamel(k)] = v;
  return out;
};

// Colunas que só o servidor escreve. Guardam provider_id, link de documentos e
// o que cada sistema respondeu; o navegador carrega uma cópia no load da página
// e, ao salvar qualquer edição, gravaria essa cópia por cima do que a serverless
// acabou de escrever.
//
// `integracao_sync` estava faltando nesta lista — a coluna nasceu com esse nome
// quando a integração passou a atender os três sistemas, e a proteção continuou
// apontando só para o nome antigo. Efeito: concluir uma ficha apagava o
// resultado da sincronização que ela mesma tinha acabado de disparar (link de
// documentos, docs_faltantes, assinatura dispensada) — e a tela do vínculo
// voltava a dizer que nada tinha acontecido.
const SOMENTE_SERVIDOR = ["manutencao_sync", "integracao_sync"];
// Colunas geradas pelo banco também nunca vão: ver colunasGeradas.js.

// ── Trava: um campo desconhecido não derruba a linha inteira ──
//
// Quando uma coluna não existe, o Postgres recusa TUDO — foi isso que fez a
// ficha inteira sumir por causa de um único campo com nome errado. A regra
// aqui inverte o estrago: o campo sem coluna fica de fora e o resto grava,
// com aviso na tela e no console para alguém corrigir.
//
// As colunas de cada tabela são aprendidas do próprio carregamento inicial —
// toda linha que vem do banco carrega os nomes de todas as colunas. Custo
// zero: nenhuma consulta a mais. Tabela vazia não ensina nada, e aí não se
// filtra (comportamento antigo, sem palpite).
const COLUNAS_CONHECIDAS = {};

export const registrarColunas = (table, linhaBruta) => {
  if (!linhaBruta || COLUNAS_CONHECIDAS[table]) return;
  COLUNAS_CONHECIDAS[table] = new Set(Object.keys(linhaBruta));
};

const podar = (table, linha) => {
  const cols = COLUNAS_CONHECIDAS[table];
  if (!cols) return { linha, ignorados: [] };
  const out = {}, ignorados = [];
  for (const [k, v] of Object.entries(linha)) (cols.has(k) ? out[k] = v : ignorados.push(k));
  return { linha: out, ignorados };
};

const rowToDb = (obj, userId, table) => {
  if (!obj) return null;
  const bruto = {};
  for (const [k, v] of Object.entries(obj)) bruto[ALIAS_COLUNA[k] || toSnake(k)] = v;
  for (const c of SOMENTE_SERVIDOR) delete bruto[c];
  // Na criação, o dono é quem está gravando — a menos que a linha diga outro.
  // `userId: null` explícito é "sem responsável" (empresa aberta para o time,
  // ex.: registro rápido do admin) e não pode virar o id de quem clicou.
  if (userId && obj.userId === undefined) bruto.user_id = userId;

  const { linha, ignorados } = podar(table, semColunasGeradas(table, bruto));
  if (ignorados.length) {
    console.error(`[${table}] campo sem coluna no banco, gravação seguiu sem ele:`, ignorados.join(", "));
    window.dispatchEvent(new CustomEvent("crm-campo-desconhecido", { detail: { tabela: table, campos: ignorados } }));
  }
  return linha;
};

// Grava UMA linha agora, sem esperar o ciclo de sync do hook. Existe para o
// caso em que o passo seguinte é o SERVIDOR ler essa linha (avisar a
// manutenção) — aí ela precisa estar no banco, não só no estado do React.
// O sync do hook roda depois e não duplica: upsert por id.
export async function upsertRow(table, obj, userId) {
  const { error } = await supabase.from(table).upsert(rowToDb(obj, userId, table), { onConflict: "id" });
  if (error) {
    // Guarda para reenviar sozinho na próxima abertura — quem chamou mostra o
    // erro, mas o dado não depende mais de a pessoa estar olhando a tela.
    enfileirar(table, obj, { userId });
    throw new Error(error.message);
  }
}

// ── Leitura (paginada — sem limite de 1000 linhas) ──
export async function fetchAll(table) {
  const PAGE_SIZE = 1000;
  let allData = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select("*")
      .range(from, from + PAGE_SIZE - 1)
      .order("id", { ascending: true });

    if (error) {
      console.error(`Erro ao carregar ${table}:`, error.message);
      // Tabela que não existe no banco (PGRST205) é uma classe de erro à parte:
      // não é instabilidade, é o app gravando num lugar que nunca foi criado.
      // Foi assim que `management_reports` passou meses engolindo todo relatório
      // do módulo Gestão — o erro morria no console e a tela dizia "salvo".
      if (error.code === "PGRST205" || /Could not find the table/i.test(error.message || "")) {
        window.dispatchEvent(new CustomEvent("crm-tabela-inexistente", {
          detail: { tabela: table, erro: error.message },
        }));
      }
      return allData.map(rowFromDb);
    }

    if (data && data.length > 0) {
      // A primeira linha ensina os nomes das colunas desta tabela (ver `podar`)
      registrarColunas(table, data[0]);
      allData = allData.concat(data);
    }

    if (!data || data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return allData.map(rowFromDb);
}

// ── Uma linha só, relida do banco ──
// Usada pelo tempo real: o aviso de mudança serve só como SINAL (o id), e a
// linha completa vem daqui. Linha de parceiro carrega o histórico inteiro de
// conversas em JSONB — confiar no conteúdo do aviso seria apostar que ele
// sempre cabe no limite da mensagem.
export async function fetchRow(table, id) {
  const { data, error } = await supabase.from(table).select("*").eq("id", id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? rowFromDb(data) : null;
}

// ── Sync: compara arrays antigo/novo e envia diferenças ao Supabase ──
export async function syncChanges(table, oldData, newData, userId) {
  if (!oldData || !newData) return;

  const oldMap = new Map(oldData.map((d) => [d.id, d]));
  const newMap = new Map(newData.map((d) => [d.id, d]));

  const promises = [];

  // Inserções — usa upsert para evitar conflito de id duplicado.
  // Deduplica por id: se o estado local tiver o mesmo id duas vezes (ex.: salvar
  // a mesma ficha nova duas vezes), um upsert com id repetido no MESMO lote
  // falha ("ON CONFLICT ... cannot affect row a second time") e derrubava toda a
  // gravação — inclusive das outras linhas. Fica com a última ocorrência.
  const added = [...new Map(newData.filter((d) => !oldMap.has(d.id)).map((d) => [d.id, d])).values()];
  if (added.length > 0) {
    const rows = added.map((d) => rowToDb(d, userId, table));
    promises.push(
      supabase.from(table).upsert(rows, { onConflict: "id" })
    );
  }

  // Deleções
  const deleted = oldData.filter((d) => !newMap.has(d.id));
  if (deleted.length > 0) {
    const ids = deleted.map((d) => d.id);
    promises.push(supabase.from(table).delete().in("id", ids));
  }

  // Atualizações
  const updated = newData.filter((d) => {
    const old = oldMap.get(d.id);
    return old && JSON.stringify(old) !== JSON.stringify(d);
  });
  for (const item of updated) {
    const row = rowToDb(item, null, table);
    delete row.id;
    // O dono NÃO viaja em toda gravação: `user_id` sairia com o id de quem está
    // com a tela aberta, e o primeiro gerente a mexer numa empresa roubaria a
    // carteira do comercial sem querer. Ele só viaja quando MUDOU de verdade —
    // é assim que a transferência de carteira e o "assumi este contato"
    // conseguem gravar, em vez de sumir na volta.
    const antigo = oldMap.get(item.id);
    if (!("userId" in item) || antigo?.userId === item.userId) delete row.user_id;
    promises.push(supabase.from(table).update(row).eq("id", item.id));
  }

  const results = await Promise.allSettled(promises);
  let hasError = false;
  results.forEach((r) => {
    if (r.status === "fulfilled" && r.value?.error) {
      console.error(`Sync error (${table}):`, r.value.error.message);
      hasError = true;
    }
    if (r.status === "rejected") {
      console.error(`Sync rejected (${table}):`, r.reason);
      hasError = true;
    }
  });
  if (hasError) {
    // O que estava indo agora fica guardado no navegador. Antes, uma falha aqui
    // dependia de a pessoa ver o aviso e refazer na hora — se ela fechasse a
    // aba, o trabalho ia junto.
    enfileirarVarios(table, added, { userId });
    enfileirarVarios(table, updated, { userId });
    throw new Error(`Sync failed for ${table}`);
  }
}

/**
 * Reenvia o que ficou na fila (falhas anteriores). Chamado na abertura do app
 * e do formulário público — é o momento em que a rede costuma estar de volta.
 *
 * Devolve { enviados, restantes }.
 */
export function reenviarPendentes() {
  return enviarPendentes(rowToDb);
}
